import {
  EmployeePicker,
  createEmptySelection,
  type PickerSelection,
} from '@/components/employee-picker';
import { getEmployees } from '@/lib/employees';
import type { EmployeeListItem } from '@/types/employee';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ApproversSection } from '@/features/absences/policy-configurator/components';
import {
  assignAbsenceTypeToEmploymentType,
  batchAssignAbsenceType,
  getAbsenceType,
  getAbsenceTypes,
  getAbsenceTypeAssignments,
  migrateBalanceBetweenAbsenceTypes,
  unassignAbsenceTypeFromEmploymentType,
  unassignEmployeeFromAbsenceType,
  updateAbsenceType,
  type AbsenceType,
  type UpdateAbsenceTypeInput,
} from '@/lib/absences';
import { useEmploymentTypes } from '@/hooks/use-organizations';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useParams } from '@tanstack/react-router';
import { ArrowRight, ArrowRightLeft, Check, Loader2, Save, Search, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { toast } from 'sonner';
import axios from 'axios';

// Helper function to normalize number input values
// Removes trailing zeros and decimal point without digits after it
function normalizeNumberValue(value: string | number, allowDecimals: boolean = true): number {
  if (value === '' || value === null || value === undefined) {
    return 0;
  }

  const str = String(value);

  // Remove trailing zeros and decimal point if no digits after it
  const normalized = str.replace(/\.0+$/, '').replace(/\.$/, '');

  // If empty after normalization, return 0
  if (normalized === '' || normalized === '-') {
    return 0;
  }

  const num = parseFloat(normalized);

  // If not allowing decimals and we have a decimal, round it
  if (!allowDecimals && !Number.isInteger(num)) {
    return Math.round(num);
  }

  return isNaN(num) ? 0 : num;
}

const routePath = '/settings/absence-types/$id/configure' as const;
export const Route = createFileRoute(routePath)({
  component: AbsenceTypeConfiguratorPage,
});

// Tab configuration with progress tracking
const TABS = [
  { id: 'basics', label: 'Basics', description: 'Name, type, and general settings' },
  { id: 'accrual', label: 'Accrual', description: 'How balance is earned' },
  { id: 'limits', label: 'Limits', description: 'Balance caps and carry-over' },
  { id: 'requests', label: 'Requests', description: 'Request rules and restrictions' },
  { id: 'approvals', label: 'Approvals', description: 'Approval workflow' },
  { id: 'notifications', label: 'Notifications', description: 'Alerts and visibility' },
  { id: 'assignments', label: 'Assignments', description: 'Assign to employees and groups' },
] as const;

type TabId = (typeof TABS)[number]['id'];

// Build initial form data from absence type
function buildInitialFormData(a: AbsenceType): UpdateAbsenceTypeInput {
  return {
    // Basics
    name: a.name,
    color: a.color || '#9E9E9E',
    isPaid: a.isPaid,
    defaultDays: a.defaultDays,
    countingMethod: a.countingMethod,
    unlimitedBalance: a.unlimitedBalance,

    // Accrual
    accrualEnabled: a.accrualEnabled,
    accrualAmount: a.accrualAmount,
    accrualIntervalCount: a.accrualIntervalCount ?? 1,
    accrualInterval: a.accrualInterval,
    accrualTiming: a.accrualTiming,
    accrualYearType: a.accrualYearType,
    firstAccrualCalculation: a.firstAccrualCalculation,
    startAccrual: a.startAccrual,
    startDelayDays: a.startDelayDays,
    countProbationInAccrual: a.countProbationInAccrual,
    initialBalance: a.initialBalance,

    // Limits
    maxBalance: a.maxBalance,
    allowNegativeBalance: a.allowNegativeBalance,
    allowAdvanceAgainstAccrual: a.allowAdvanceAgainstAccrual,
    carryOverModel: a.carryOverModel,
    // `null` is the intentional "infinite availability" sentinel (Accrued
    // days never expire). The `??` operator catches both `null` and
    // `undefined`, so `?? 365` silently flipped the "Infinite availability"
    // toggle OFF every time the form re-initialized from server data —
    // both on initial load and right after save (`handleSave` re-runs
    // this builder with the server-returned record). Preserve `null` and
    // only default truly-missing (undefined) values to 365.
    availabilityPeriodDays: a.availabilityPeriodDays === undefined ? 365 : a.availabilityPeriodDays,
    yearBasedAvailability: a.yearBasedAvailability,
    afterAvailabilityWindow: a.afterAvailabilityWindow,
    carryOverLimit: a.carryOverLimit || 10,
    carriedDaysExpireAfterDays: a.carriedDaysExpireAfterDays || 90,

    // Requests
    enableOnDemand: a.enableOnDemand,
    onDemandAmount: a.onDemandAmount,
    preventOverlaps: a.preventOverlaps,
    allowHalfDays: a.allowHalfDays,
    minRequestAmount: a.minRequestAmount,
    maxRequestAmount: a.maxRequestAmount,
    minNoticeDays: a.minNoticeDays,
    maxAdvanceDays: a.maxAdvanceDays,
    allowedDuringProbation: a.allowedDuringProbation,
    probationLimit: a.probationLimit,
    employeeCanSubmit: a.employeeCanSubmit,

    // Approvals
    approvalRequired: a.approvalRequired,
    managerCanApprove: a.managerCanApprove,
    hrCanApprove: a.hrCanApprove ?? false,
    specificApproversEnabled: a.specificApproversEnabled ?? false,
    substituteApproversEnabled: a.substituteApproversEnabled ?? false,
    autoApproveIfNoApprover: a.autoApproveIfNoApprover,
    autoApproveAfterDays: a.autoApproveAfterDays,

    // Notifications
    showInCalendar: a.showInCalendar,
    emailNotifications: a.emailNotifications,
    notifyApprovers: a.notifyApprovers,
    notifyRequester: a.notifyRequester,
    attachmentMode: a.attachmentMode ?? 'optional',
    allowRecall: a.allowRecall,
    recallDeadlineDays: a.recallDeadlineDays,
    requesterCommentRequired: a.requesterCommentRequired,
    approverCommentRequired: a.approverCommentRequired,
  };
}

function AbsenceTypeConfiguratorPage() {
  const routePathForParams = '/settings/absence-types/$id/configure' as const;
  const { id } = useParams({ from: routePathForParams });
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('basics');

  // Load absence type (now contains all settings)
  const {
    data: absenceType,
    isLoading: isLoadingType,
    error: typeError,
  } = useQuery({
    queryKey: ['absence-types', id],
    queryFn: () => getAbsenceType(id),
  });

  // Update absence type mutation
  const updateAbsenceTypeMutation = useMutation({
    mutationFn: (data: UpdateAbsenceTypeInput) => updateAbsenceType(id, data),
    onSuccess: (updatedAbsenceType) => {
      queryClient.setQueryData(['absence-types', id], updatedAbsenceType);
      queryClient.invalidateQueries({ queryKey: ['absence-types'] });
      // Invalidate employee assigned absence types so the request dialog updates immediately
      queryClient.invalidateQueries({ queryKey: ['employee-assigned-absence-types'] });
      toast.success('Saved');
    },
    onError: (err: unknown) => {
      let message = 'Failed to save absence type';
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as { message?: unknown } | undefined;
        if (Array.isArray(data?.message)) {
          message = data.message.join('; ');
        } else if (typeof data?.message === 'string') {
          message = data.message;
        } else if (err.response?.status) {
          message = `${message} (HTTP ${err.response.status})`;
        }
      } else if (err instanceof Error) {
        message = err.message;
      }
      // eslint-disable-next-line no-console
      console.error('updateAbsenceType failed:', err);
      toast.error(message, { duration: 8000 });
    },
  });

  const isLoading = isLoadingType;
  const error = typeError;

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !absenceType) {
    return (
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-4xl mx-auto">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-destructive mb-4">
                {error
                  ? `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
                  : 'Absence type not found'}
              </p>
              <Button asChild variant="outline" className="cursor-pointer">
                <Link to="/settings/absence-types">Back to Absence Types</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <AbsenceTypeConfiguratorContent
      absenceType={absenceType}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      updateAbsenceTypeMutation={updateAbsenceTypeMutation}
    />
  );
}

// Inner content component — only renders once we have absenceType,
// so we can safely initialize lifted form state from it.
function AbsenceTypeConfiguratorContent({
  absenceType,
  activeTab,
  setActiveTab,
  updateAbsenceTypeMutation,
}: {
  absenceType: AbsenceType;
  activeTab: TabId;
  setActiveTab: (id: TabId) => void;
  updateAbsenceTypeMutation: {
    mutateAsync: (data: UpdateAbsenceTypeInput) => Promise<AbsenceType>;
    isPending: boolean;
  };
}) {
  const [formData, setFormData] = useState<UpdateAbsenceTypeInput>(() =>
    buildInitialFormData(absenceType)
  );
  const [originalData, setOriginalData] = useState<UpdateAbsenceTypeInput>(() =>
    buildInitialFormData(absenceType)
  );

  // Re-sync when the absence type query refetches (e.g., after save).
  useEffect(() => {
    const fresh = buildInitialFormData(absenceType);
    setFormData(fresh);
    setOriginalData(fresh);
  }, [absenceType]);

  const updateField = useCallback(
    <K extends keyof UpdateAbsenceTypeInput>(key: K, value: UpdateAbsenceTypeInput[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const hasChanges = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(originalData),
    [formData, originalData]
  );

  const handleSave = async () => {
    const updated = await updateAbsenceTypeMutation.mutateAsync(formData);
    // Use the server-returned record as the new baseline so future hasChanges
    // checks compare against what's actually persisted.
    const fresh = buildInitialFormData(updated);
    setFormData(fresh);
    setOriginalData(fresh);
  };

  const handleDiscard = () => {
    setFormData(originalData);
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <div
                className="w-3 h-3 rounded-full shrink-0"
                style={{ backgroundColor: absenceType.color || '#9E9E9E' }}
              />
              <h1 className="text-2xl font-semibold tracking-tight text-foreground truncate">
                {absenceType.name}
              </h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Configure settings for this absence type
            </p>
          </div>
        </div>

        {/* Configurator Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as TabId)}
          className="space-y-6"
        >
          <div className="flex items-end justify-between gap-4 border-b border-border">
            <TabsList className="h-auto bg-transparent p-0 gap-6 justify-start flex-wrap rounded-none">
              {TABS.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="relative flex-none rounded-none bg-transparent shadow-none px-0 pb-3 pt-0 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none cursor-pointer transition-colors after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-hygge after:rounded-t-full after:opacity-0 data-[state=active]:after:opacity-100"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Save / Discard actions — pinned to the tab bar */}
            {hasChanges && (
              <TooltipProvider delayDuration={200}>
                <div className="flex items-center gap-1 shrink-0 pb-1.5">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleDiscard}
                        disabled={updateAbsenceTypeMutation.isPending}
                        className="h-8 w-8 cursor-pointer text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Discard changes</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        onClick={handleSave}
                        disabled={updateAbsenceTypeMutation.isPending}
                        className="h-8 w-8 cursor-pointer"
                      >
                        {updateAbsenceTypeMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Save changes</TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            )}
          </div>

          {/* Tab Contents */}
          <TabsContent value="basics" className="mt-0">
            <TabCard
              title="Basics"
              description="Configure the fundamental settings for this absence type"
            >
              <BasicsTabContent formData={formData} onFieldChange={updateField} />
            </TabCard>
          </TabsContent>

          <TabsContent value="accrual" className="mt-0">
            <TabCard
              title="Accrual Settings"
              description="Define how employees earn their absence balance"
            >
              <AccrualTabContent formData={formData} onFieldChange={updateField} />
            </TabCard>
          </TabsContent>

          <TabsContent value="limits" className="mt-0">
            <TabCard title="Balance Limits" description="Set maximum balance and carry-over rules">
              <LimitsTabContent formData={formData} onFieldChange={updateField} />
            </TabCard>
          </TabsContent>

          <TabsContent value="requests" className="mt-0">
            <TabCard
              title="Request Rules"
              description="Configure restrictions for absence requests"
            >
              <RequestsTabContent
                formData={formData}
                onFieldChange={updateField}
                countingMethod={absenceType.countingMethod}
              />
            </TabCard>
          </TabsContent>

          <TabsContent value="approvals" className="mt-0">
            <TabCard
              title="Approval Workflow"
              description="Set up the approval process for requests"
            >
              <ApprovalsTabContent
                formData={formData}
                onFieldChange={updateField}
                absenceTypeId={absenceType.id}
              />
            </TabCard>
          </TabsContent>

          <TabsContent value="notifications" className="mt-0">
            <TabCard
              title="Notifications & Visibility"
              description="Configure alerts and calendar display options"
            >
              <NotificationsTabContent formData={formData} onFieldChange={updateField} />
            </TabCard>
          </TabsContent>

          <TabsContent value="assignments" className="mt-0">
            <TabCard title="Assignments" description="Assign this absence type to employees">
              <AssignmentsTabContent absenceType={absenceType} />
            </TabCard>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// Reusable card wrapper for tab content
function TabCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-lg">{title}</CardTitle>
        <p className="text-sm text-muted-foreground">{description}</p>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// Color presets for absence types
const COLOR_PRESETS = [
  '#4CAF50', // Green - Vacation
  '#2196F3', // Blue - Sick leave
  '#FF9800', // Orange - Personal
  '#9C27B0', // Purple - Training
  '#F44336', // Red - Emergency
  '#607D8B', // Grey - Other
  '#00BCD4', // Cyan - Remote
  '#E91E63', // Pink - Maternity
];

// Tab content props interface — every form tab is controlled.
// Form fields live on the page; tabs read from formData and emit changes
// via onFieldChange. The page collects everything into a single save.
interface TabContentProps {
  formData: UpdateAbsenceTypeInput;
  onFieldChange: <K extends keyof UpdateAbsenceTypeInput>(
    key: K,
    value: UpdateAbsenceTypeInput[K]
  ) => void;
}

function BasicsTabContent({ formData, onFieldChange }: TabContentProps) {
  const [errors, setErrors] = useState<{ defaultDays?: string }>({});

  const unlimitedBalance = formData.unlimitedBalance ?? false;
  const defaultDays = formData.defaultDays ?? 0;
  const color = formData.color ?? '#9E9E9E';

  return (
    <div className="divide-y">
      {/* Name */}
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-5 first:pt-0">
        <div>
          <Label htmlFor="name" className="text-sm font-medium">
            Name
          </Label>
          <p className="text-xs text-muted-foreground mt-1">
            The display name for this absence type
          </p>
        </div>
        <div className="max-w-md">
          <Input
            id="name"
            value={formData.name ?? ''}
            onChange={(e) => onFieldChange('name', e.target.value)}
            placeholder="e.g., Annual Leave"
          />
        </div>
      </div>

      {/* Color */}
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-5">
        <div>
          <Label className="text-sm font-medium">Color</Label>
          <p className="text-xs text-muted-foreground mt-1">Shown in calendars and reports</p>
        </div>
        <div className="flex items-center gap-3 max-w-md flex-wrap">
          <div className="flex gap-2">
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => onFieldChange('color', preset)}
                className={`w-7 h-7 rounded-full border-2 transition-all cursor-pointer ${
                  color === preset
                    ? 'border-foreground scale-110'
                    : 'border-transparent hover:scale-105'
                }`}
                style={{ backgroundColor: preset }}
                title={preset}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="color"
              value={color}
              onChange={(e) => onFieldChange('color', e.target.value)}
              className="w-9 h-9 p-1 cursor-pointer"
            />
            <span className="text-xs text-muted-foreground font-mono">{color}</span>
          </div>
        </div>
      </div>

      {/* Unlimited Days Toggle */}
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-5">
        <div>
          <Label htmlFor="unlimitedBalance" className="text-sm font-medium">
            Unlimited days per year
          </Label>
          <p className="text-xs text-muted-foreground mt-1">
            No balance limit; employees can take unlimited time off
          </p>
        </div>
        <div className="max-w-md">
          <Switch
            id="unlimitedBalance"
            checked={unlimitedBalance}
            onCheckedChange={(checked) => {
              onFieldChange('unlimitedBalance', checked);
              if (checked && errors.defaultDays) {
                setErrors((prev) => ({ ...prev, defaultDays: undefined }));
              }
            }}
          />
        </div>
      </div>

      {/* Default Days */}
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-5">
        <div>
          <Label htmlFor="defaultDays" className="text-sm font-medium">
            Default days per year {!unlimitedBalance && '*'}
          </Label>
          <p className="text-xs text-muted-foreground mt-1">
            {unlimitedBalance
              ? 'Not applicable when unlimited days is enabled'
              : 'Initial balance for new employees (can be overridden per employee)'}
          </p>
        </div>
        <div className="max-w-md">
          <div className="flex items-center gap-2">
            <Input
              id="defaultDays"
              type="number"
              min={0}
              max={365}
              value={defaultDays === 0 ? '' : defaultDays}
              onChange={(e) => {
                const value = e.target.value;
                if (errors.defaultDays) {
                  setErrors((prev) => ({ ...prev, defaultDays: undefined }));
                }
                if (value === '') {
                  onFieldChange('defaultDays', 0);
                } else {
                  const num = parseInt(value);
                  onFieldChange('defaultDays', isNaN(num) ? 0 : num);
                }
              }}
              onBlur={(e) => {
                const normalized = normalizeNumberValue(e.target.value, false);
                const finalValue = Math.max(0, Math.min(365, normalized));
                onFieldChange('defaultDays', finalValue);
                if (!unlimitedBalance && (!finalValue || finalValue <= 0)) {
                  setErrors((prev) => ({
                    ...prev,
                    defaultDays: 'Default days per year must be greater than 0',
                  }));
                } else {
                  setErrors((prev) => ({ ...prev, defaultDays: undefined }));
                }
              }}
              disabled={unlimitedBalance}
              className={`w-28 ${errors.defaultDays ? 'border-destructive' : ''} ${unlimitedBalance ? 'opacity-50' : ''}`}
            />
            <span className="text-sm text-muted-foreground">days / year</span>
          </div>
          {errors.defaultDays && (
            <p className="text-xs text-destructive mt-1.5">{errors.defaultDays}</p>
          )}
        </div>
      </div>

      {/* Paid/Unpaid Toggle */}
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-5">
        <div>
          <Label htmlFor="isPaid" className="text-sm font-medium">
            Paid absence
          </Label>
          <p className="text-xs text-muted-foreground mt-1">
            Employees receive salary during this absence
          </p>
        </div>
        <div className="max-w-md">
          <Switch
            id="isPaid"
            checked={formData.isPaid ?? false}
            onCheckedChange={(checked) => onFieldChange('isPaid', checked)}
          />
        </div>
      </div>

      {/* Counting Method */}
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-5">
        <div>
          <Label htmlFor="countingMethod" className="text-sm font-medium">
            Counting method
          </Label>
          <p className="text-xs text-muted-foreground mt-1">How absence duration is calculated</p>
        </div>
        <div className="max-w-md">
          <Select
            value={formData.countingMethod}
            onValueChange={(value) =>
              onFieldChange('countingMethod', value as 'working_days' | 'calendar_days')
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select counting method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="working_days">Working days</SelectItem>
              <SelectItem value="calendar_days">Calendar days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

// Accrual intervals
const ACCRUAL_INTERVALS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'half_year', label: 'Half Year' },
  { value: 'year', label: 'Year' },
] as const;

// Accrual tab content
function AccrualTabContent({ formData, onFieldChange }: TabContentProps) {
  // Aliases for cleaner template
  const accrualEnabled = formData.accrualEnabled ?? false;
  const accrualAmount = formData.accrualAmount ?? 0;
  const accrualIntervalCount = formData.accrualIntervalCount ?? 1;
  const accrualInterval = formData.accrualInterval ?? 'year';
  const accrualTiming = formData.accrualTiming ?? 'start';
  const yearType = formData.accrualYearType ?? 'calendar';
  const firstAccrual = formData.firstAccrualCalculation ?? 'proportional';
  const startCondition = formData.startAccrual ?? 'immediately';
  const delayDays = formData.startDelayDays ?? 0;
  const initialBalance = formData.initialBalance ?? 0;

  return (
    <div className="space-y-10">
      {/* Enable Automatic Accrual */}
      <div className="divide-y border-t border-b">
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
          <div>
            <Label htmlFor="accrualEnabled" className="text-sm font-medium">
              Enable automatic accrual
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              System automatically adds balance based on rules below
            </p>
          </div>
          <div className="max-w-md">
            <Switch
              id="accrualEnabled"
              checked={accrualEnabled}
              onCheckedChange={(v) => onFieldChange('accrualEnabled', v)}
            />
          </div>
        </div>
      </div>

      {accrualEnabled ? (
        <>
          {/* Accrual Frequency */}
          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold tracking-tight">Accrual Frequency</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                How many days are added each accrual period
              </p>
            </div>
            <div className="divide-y border-t border-b">
              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label className="text-sm font-medium">Schedule</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Use 0.5, 1, 1.5, 2, etc. e.g. "20 days every 1 Year" = 20 days annually
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-muted-foreground">Accrue</span>
                    <Input
                      type="number"
                      min={0}
                      step={0.5}
                      value={accrualAmount === 0 ? '' : accrualAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          onFieldChange('accrualAmount', 0);
                        } else {
                          const num = parseFloat(value);
                          onFieldChange('accrualAmount', isNaN(num) ? 0 : num);
                        }
                      }}
                      onBlur={(e) => {
                        const normalized = normalizeNumberValue(e.target.value, true);
                        onFieldChange('accrualAmount', Math.max(0, normalized));
                      }}
                      className="w-20"
                    />
                    <span className="text-sm text-muted-foreground">days every</span>
                    <Input
                      type="number"
                      min={0.5}
                      step={0.5}
                      value={accrualIntervalCount === 0 ? '' : accrualIntervalCount}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow any input, store as number
                        if (value === '') {
                          onFieldChange('accrualIntervalCount', 0);
                        } else {
                          const num = parseFloat(value);
                          onFieldChange('accrualIntervalCount', isNaN(num) ? 0 : num);
                        }
                      }}
                      onBlur={(e) => {
                        const normalized = normalizeNumberValue(e.target.value, true);
                        // Round to nearest 0.5 and ensure min 0.5
                        const rounded = Math.round(normalized * 2) / 2;
                        onFieldChange('accrualIntervalCount', Math.max(0.5, rounded));
                      }}
                      className="w-20"
                    />
                    <select
                      value={accrualInterval}
                      onChange={(e) =>
                        onFieldChange(
                          'accrualInterval',
                          e.target.value as UpdateAbsenceTypeInput['accrualInterval']
                        )
                      }
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm cursor-pointer"
                    >
                      {ACCRUAL_INTERVALS.map((interval) => (
                        <option key={interval.value} value={interval.value}>
                          {interval.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label className="text-sm font-medium">When to add balance</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Balance can be added at the start or end of each period
                  </p>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 max-w-md">
                  <button
                    type="button"
                    onClick={() => onFieldChange('accrualTiming', 'start')}
                    className={`px-3 py-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                      accrualTiming === 'start'
                        ? 'border-hygge bg-hygge/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="font-medium text-sm">Start of Period</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Balance added upfront (advance)
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => onFieldChange('accrualTiming', 'end')}
                    className={`px-3 py-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                      accrualTiming === 'end'
                        ? 'border-hygge bg-hygge/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="font-medium text-sm">End of Period</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Balance added after working
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Accrual Year */}
          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold tracking-tight">Accrual Year</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                How the accrual year is defined for each employee
              </p>
            </div>
            <div className="divide-y border-t border-b">
              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label className="text-sm font-medium">Year type</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Anchor accrual to start date or calendar year
                  </p>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 max-w-md">
                  <button
                    type="button"
                    onClick={() => onFieldChange('accrualYearType', 'individual')}
                    className={`px-3 py-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                      yearType === 'individual'
                        ? 'border-hygge bg-hygge/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="font-medium text-sm">Individual</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Based on employee start date
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => onFieldChange('accrualYearType', 'calendar')}
                    className={`px-3 py-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                      yearType === 'calendar'
                        ? 'border-hygge bg-hygge/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="font-medium text-sm">Calendar</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      January to December
                    </div>
                  </button>
                </div>
              </div>

              {yearType === 'calendar' && (
                <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                  <div>
                    <Label className="text-sm font-medium">First year calculation</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      How balance is awarded for the partial first year
                    </p>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2 max-w-md">
                    <button
                      type="button"
                      onClick={() => onFieldChange('firstAccrualCalculation', 'full')}
                      className={`px-3 py-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                        firstAccrual === 'full'
                          ? 'border-hygge bg-hygge/5'
                          : 'border-border hover:bg-muted/50'
                      }`}
                    >
                      <div className="font-medium text-sm">Full Amount</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Get full balance regardless of start date
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => onFieldChange('firstAccrualCalculation', 'proportional')}
                      className={`px-3 py-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                        firstAccrual === 'proportional'
                          ? 'border-hygge bg-hygge/5'
                          : 'border-border hover:bg-muted/50'
                      }`}
                    >
                      <div className="font-medium text-sm">Proportional</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Based on remaining time in year
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* When Accrual Starts */}
          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold tracking-tight">When Accrual Starts</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Trigger that begins the accrual schedule for an employee
              </p>
            </div>
            <div className="divide-y border-t border-b">
              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label className="text-sm font-medium">Start condition</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Begin accruing immediately, after probation, or after a delay
                  </p>
                </div>
                <div className="space-y-2 max-w-md">
                  <button
                    type="button"
                    onClick={() => onFieldChange('startAccrual', 'immediately')}
                    className={`w-full px-3 py-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                      startCondition === 'immediately'
                        ? 'border-hygge bg-hygge/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="font-medium text-sm">Immediately</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Start accruing from hire date
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => onFieldChange('startAccrual', 'after_probation')}
                    className={`w-full px-3 py-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                      startCondition === 'after_probation'
                        ? 'border-hygge bg-hygge/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="font-medium text-sm">After Probation</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Start after probation period ends
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => onFieldChange('startAccrual', 'after_delay')}
                    className={`w-full px-3 py-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                      startCondition === 'after_delay'
                        ? 'border-hygge bg-hygge/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="font-medium text-sm">After Delay</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Start after specified number of days
                    </div>
                  </button>
                </div>
              </div>

              {startCondition === 'after_delay' && (
                <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                  <div>
                    <Label htmlFor="startDelayDays" className="text-sm font-medium">
                      Delay length
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      How many days after hire before accrual begins
                    </p>
                  </div>
                  <div className="max-w-md">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Wait</span>
                      <Input
                        id="startDelayDays"
                        type="number"
                        min={1}
                        value={delayDays === 0 ? '' : delayDays}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            onFieldChange('startDelayDays', 0);
                          } else {
                            const num = parseInt(value);
                            onFieldChange('startDelayDays', isNaN(num) ? 0 : num);
                          }
                        }}
                        onBlur={(e) => {
                          const normalized = normalizeNumberValue(e.target.value, false);
                          onFieldChange('startDelayDays', Math.max(1, normalized));
                        }}
                        className="w-20"
                      />
                      <span className="text-sm text-muted-foreground">days after hire</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Initial Balance */}
          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold tracking-tight">Initial Balance</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                One-time balance added when an employee is assigned this absence type
              </p>
            </div>
            <div className="divide-y border-t border-b">
              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label htmlFor="initialBalance" className="text-sm font-medium">
                    Initial balance
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Added in addition to regular accrual
                  </p>
                </div>
                <div className="max-w-md">
                  <div className="flex items-center gap-2">
                    <Input
                      id="initialBalance"
                      type="number"
                      min={0}
                      step={0.5}
                      value={initialBalance.toString()}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || value === '-') {
                          onFieldChange('initialBalance', 0);
                        } else {
                          const num = parseFloat(value);
                          if (!isNaN(num) && num >= 0) {
                            onFieldChange('initialBalance', num);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        const normalized = normalizeNumberValue(e.target.value, true);
                        onFieldChange('initialBalance', Math.max(0, normalized));
                      }}
                      className="w-24"
                    />
                    <span className="text-sm text-muted-foreground">days</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      ) : (
        <div className="border-t border-b py-6 text-center">
          <p className="text-xs text-muted-foreground">
            Automatic accrual is disabled. Balance will be managed manually for each employee.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Useful for unpaid absences, contractors, or one-time allowances.
          </p>
        </div>
      )}
    </div>
  );
}

// Carry-over models
const CARRY_OVER_MODELS = [
  {
    value: 'ttl',
    label: 'Fixed Availability (TTL)',
    description: 'Days expire after a set period from accrual',
  },
  {
    value: 'year_based',
    label: 'Year-Based',
    description: 'Days available until end of next year',
  },
] as const;

// Limits tab content
function LimitsTabContent({ formData, onFieldChange }: TabContentProps) {
  // Aliases for cleaner template
  const unlimitedBalance = formData.unlimitedBalance ?? false;
  const maxBalance = formData.maxBalance ?? 0;
  const allowNegativeBalance = formData.allowNegativeBalance ?? false;
  const allowAdvanceAgainstAccrual = formData.allowAdvanceAgainstAccrual ?? false;
  const carryOverModel = formData.carryOverModel ?? 'ttl';
  const yearBasedWindow = formData.yearBasedAvailability ?? 'end_of_next_calendar';
  const afterWindow = formData.afterAvailabilityWindow ?? 'expire';
  const carryOverLimit = formData.carryOverLimit ?? 10;
  const carriedDaysExpire = formData.carriedDaysExpireAfterDays ?? 90;
  const isAvailabilityPeriodInfinite = formData.availabilityPeriodDays == null;

  return (
    <div className="space-y-10">
      {/* Maximum Balance */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Maximum Balance</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Whether and at what point accrual stops
          </p>
        </div>
        <div className="divide-y border-t border-b">
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="unlimitedBalance" className="text-sm font-medium">
                Unlimited balance
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                No cap on how many days can be accumulated
              </p>
            </div>
            <div className="max-w-md">
              <Switch
                id="unlimitedBalance"
                checked={unlimitedBalance}
                onCheckedChange={(v) => onFieldChange('unlimitedBalance', v)}
              />
            </div>
          </div>

          {!unlimitedBalance && (
            <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
              <div>
                <Label htmlFor="maxBalance" className="text-sm font-medium">
                  Balance cap
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Stop accruing once this limit is reached
                </p>
              </div>
              <div className="max-w-md">
                <div className="flex items-center gap-2">
                  <Input
                    id="maxBalance"
                    type="number"
                    min={1}
                    value={maxBalance === 0 ? '' : maxBalance}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        onFieldChange('maxBalance', 0);
                      } else {
                        const num = parseInt(value);
                        onFieldChange('maxBalance', isNaN(num) ? 0 : num);
                      }
                    }}
                    onBlur={(e) => {
                      const normalized = normalizeNumberValue(e.target.value, false);
                      onFieldChange('maxBalance', Math.max(1, normalized));
                    }}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">days maximum</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Negative Balance */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Negative Balance</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Whether employees can request more time off than they've accrued
          </p>
        </div>
        <div className="divide-y border-t border-b">
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="negativeBalance" className="text-sm font-medium">
                Allow negative balance
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Employees can take leave before they've accrued it
              </p>
            </div>
            <div className="max-w-md">
              <Switch
                id="negativeBalance"
                checked={allowNegativeBalance}
                onCheckedChange={(v) => onFieldChange('allowNegativeBalance', v)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="advanceAgainstAccrual" className="text-sm font-medium">
                Allow booking against imminent accrual
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                If an upcoming accrual lands on/before the start date and would cover the request, accept it for HR review instead of blocking it. Balance is reserved only on approval.
              </p>
            </div>
            <div className="max-w-md">
              <Switch
                id="advanceAgainstAccrual"
                checked={allowAdvanceAgainstAccrual}
                onCheckedChange={(v) => onFieldChange('allowAdvanceAgainstAccrual', v)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Carry-over Rules */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Carry-over Rules</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Define how long accrued days remain available
          </p>
        </div>
        <div className="divide-y border-t border-b">
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label className="text-sm font-medium">Carry-over model</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Choose how unused days are tracked over time
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 max-w-md">
              {CARRY_OVER_MODELS.map((model) => (
                <button
                  key={model.value}
                  type="button"
                  onClick={() => onFieldChange('carryOverModel', model.value)}
                  className={`px-3 py-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                    carryOverModel === model.value
                      ? 'border-hygge bg-hygge/5'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <div className="font-medium text-sm">{model.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{model.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* TTL Model Settings */}
          {carryOverModel === 'ttl' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label htmlFor="availabilityPeriodInfinite" className="text-sm font-medium">
                    Infinite availability
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Accrued days never expire
                  </p>
                </div>
                <div className="max-w-md">
                  <Switch
                    id="availabilityPeriodInfinite"
                    checked={isAvailabilityPeriodInfinite}
                    onCheckedChange={(checked) => {
                      // `null` is the "infinite availability" sentinel — the
                      // shared `AbsenceTypeWritable.availabilityPeriodDays`
                      // type already permits `number | null`, so no cast.
                      onFieldChange(
                        'availabilityPeriodDays',
                        checked ? null : 365
                      );
                    }}
                  />
                </div>
              </div>

              {!isAvailabilityPeriodInfinite && (
                <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                  <div>
                    <Label htmlFor="availabilityPeriodDays" className="text-sm font-medium">
                      Days available for
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Each accrued day expires individually after this period
                    </p>
                  </div>
                  <div className="max-w-md">
                    <div className="flex items-center gap-2">
                      <Input
                        id="availabilityPeriodDays"
                        type="number"
                        min={1}
                        value={
                          formData.availabilityPeriodDays === 0
                            ? ''
                            : (formData.availabilityPeriodDays ?? '')
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            onFieldChange('availabilityPeriodDays', 0);
                          } else {
                            const num = parseInt(value);
                            onFieldChange('availabilityPeriodDays', isNaN(num) ? 0 : num);
                          }
                        }}
                        onBlur={(e) => {
                          const normalized = normalizeNumberValue(e.target.value, false);
                          onFieldChange('availabilityPeriodDays', Math.max(1, normalized));
                        }}
                        className="w-20"
                      />
                      <span className="text-sm text-muted-foreground">days after accrual</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Year-based Model Settings */}
          {carryOverModel === 'year_based' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label className="text-sm font-medium">Days usable until</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    The deadline by which accrued days must be used
                  </p>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 max-w-md">
                  <button
                    type="button"
                    onClick={() => onFieldChange('yearBasedAvailability', 'end_of_next_accrual')}
                    className={`px-3 py-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                      yearBasedWindow === 'end_of_next_accrual'
                        ? 'border-hygge bg-hygge/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="font-medium text-sm">End of Next Accrual Year</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Based on employee start date
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => onFieldChange('yearBasedAvailability', 'end_of_next_calendar')}
                    className={`px-3 py-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                      yearBasedWindow === 'end_of_next_calendar'
                        ? 'border-hygge bg-hygge/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="font-medium text-sm">End of Next Calendar Year</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      December 31st of next year
                    </div>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label className="text-sm font-medium">After availability window</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    What happens to days that aren't used in time
                  </p>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 max-w-md">
                  <button
                    type="button"
                    onClick={() => onFieldChange('afterAvailabilityWindow', 'expire')}
                    className={`px-3 py-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                      afterWindow === 'expire'
                        ? 'border-hygge bg-hygge/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="font-medium text-sm">Expire</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Unused days are lost
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      onFieldChange('afterAvailabilityWindow', 'carry_over_with_limit')
                    }
                    className={`px-3 py-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                      afterWindow === 'carry_over_with_limit'
                        ? 'border-hygge bg-hygge/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="font-medium text-sm">Carry Over with Limit</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Keep some days longer
                    </div>
                  </button>
                </div>
              </div>

              {afterWindow === 'carry_over_with_limit' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                    <div>
                      <Label htmlFor="carryOverLimit" className="text-sm font-medium">
                        Carry-over limit
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Maximum number of days that can roll forward
                      </p>
                    </div>
                    <div className="max-w-md">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Up to</span>
                        <Input
                          id="carryOverLimit"
                          type="number"
                          min={1}
                          value={carryOverLimit === 0 ? '' : carryOverLimit}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              onFieldChange('carryOverLimit', 0);
                            } else {
                              const num = parseInt(value);
                              onFieldChange('carryOverLimit', isNaN(num) ? 0 : num);
                            }
                          }}
                          onBlur={(e) => {
                            const normalized = normalizeNumberValue(e.target.value, false);
                            onFieldChange('carryOverLimit', Math.max(1, normalized));
                          }}
                          className="w-20"
                        />
                        <span className="text-sm text-muted-foreground">
                          days can be carried over
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                    <div>
                      <Label htmlFor="carriedDaysExpireAfterDays" className="text-sm font-medium">
                        Carried days expire after
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Carried-over days expire after this additional period
                      </p>
                    </div>
                    <div className="max-w-md">
                      <div className="flex items-center gap-2">
                        <Input
                          id="carriedDaysExpireAfterDays"
                          type="number"
                          min={1}
                          value={carriedDaysExpire === 0 ? '' : carriedDaysExpire}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              onFieldChange('carriedDaysExpireAfterDays', 0);
                            } else {
                              const num = parseInt(value);
                              onFieldChange('carriedDaysExpireAfterDays', isNaN(num) ? 0 : num);
                            }
                          }}
                          onBlur={(e) => {
                            const normalized = normalizeNumberValue(e.target.value, false);
                            onFieldChange('carriedDaysExpireAfterDays', Math.max(1, normalized));
                          }}
                          className="w-20"
                        />
                        <span className="text-sm text-muted-foreground">days</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

// Requests tab content
function RequestsTabContent({
  formData,
  onFieldChange,
  countingMethod,
}: TabContentProps & { countingMethod: 'working_days' | 'calendar_days' }) {
  // Aliases for cleaner template
  const onDemandEnabled = formData.enableOnDemand ?? false;
  const onDemandAmount = formData.onDemandAmount ?? 0;
  const preventOverlaps = formData.preventOverlaps ?? false;
  const allowHalfDays = formData.allowHalfDays ?? false;
  const minRequestAmount = formData.minRequestAmount ?? 0;
  const maxRequestAmount = formData.maxRequestAmount;
  const minNoticeDays = formData.minNoticeDays ?? 0;
  const maxAdvanceDays = formData.maxAdvanceDays;
  const probationAllowance = formData.allowedDuringProbation ?? 'not_allowed';
  const probationLimit = formData.probationLimit ?? 0;
  const employeeCanSubmit = formData.employeeCanSubmit ?? true;

  const countingMethodLabel = countingMethod === 'working_days' ? 'Working Days' : 'Calendar Days';

  return (
    <div className="space-y-10">
      {/* Request Restrictions */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Request Restrictions</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Rules that constrain how absences can be requested
          </p>
        </div>
        <div className="divide-y border-t border-b">
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="preventOverlaps" className="text-sm font-medium">
                Prevent overlapping requests
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Block requests that overlap with existing absences
              </p>
            </div>
            <div className="max-w-md">
              <Switch
                id="preventOverlaps"
                checked={preventOverlaps}
                onCheckedChange={(v) => onFieldChange('preventOverlaps', v)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="allowHalfDays" className="text-sm font-medium">
                Allow half days
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Employees can request 0.5 day increments
              </p>
            </div>
            <div className="max-w-md">
              <Switch
                id="allowHalfDays"
                checked={allowHalfDays}
                onCheckedChange={(v) => {
                  onFieldChange('allowHalfDays', v);
                  // If disabling half days, ensure values are whole numbers
                  if (!v) {
                    const min = formData.minRequestAmount ?? 0;
                    const onDemand = formData.onDemandAmount ?? 0;
                    if (min % 1 !== 0) {
                      onFieldChange('minRequestAmount', Math.ceil(min));
                    }
                    if (onDemand % 1 !== 0) {
                      onFieldChange('onDemandAmount', Math.ceil(onDemand));
                    }
                  }
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="employeeSubmit" className="text-sm font-medium">
                Employees can submit requests
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                If disabled, only managers/HR can create requests for this type
              </p>
            </div>
            <div className="max-w-md">
              <Switch
                id="employeeSubmit"
                checked={employeeCanSubmit}
                onCheckedChange={(v) => onFieldChange('employeeCanSubmit', v)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* On-demand Absence */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">On-demand Absence</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Allow employees to take time off without prior approval
          </p>
        </div>
        <div className="divide-y border-t border-b">
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="onDemand" className="text-sm font-medium">
                Enable on-demand
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Allow taking time off without approval (within balance)
              </p>
            </div>
            <div className="max-w-md">
              <Switch
                id="onDemand"
                checked={onDemandEnabled}
                onCheckedChange={(v) => onFieldChange('enableOnDemand', v)}
              />
            </div>
          </div>

          {onDemandEnabled && (
            <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
              <div>
                <Label htmlFor="onDemandAmount" className="text-sm font-medium">
                  On-demand limit
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum days that can be taken without approval
                </p>
              </div>
              <div className="max-w-md">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Up to</span>
                  <Input
                    id="onDemandAmount"
                    type="number"
                    min={allowHalfDays ? 0.5 : 1}
                    step={allowHalfDays ? 0.5 : 1}
                    value={onDemandAmount === 0 ? '' : onDemandAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        onFieldChange('onDemandAmount', 0);
                      } else {
                        const num = parseFloat(value);
                        onFieldChange('onDemandAmount', isNaN(num) ? 0 : num);
                      }
                    }}
                    onBlur={(e) => {
                      const normalized = normalizeNumberValue(e.target.value, true);
                      if (allowHalfDays) {
                        // Round to nearest 0.5
                        const rounded = Math.round(normalized * 2) / 2;
                        onFieldChange('onDemandAmount', Math.max(0.5, rounded));
                      } else {
                        // Round to nearest whole number
                        const rounded = Math.round(normalized);
                        onFieldChange('onDemandAmount', Math.max(1, rounded));
                      }
                    }}
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">
                    days ({countingMethodLabel})
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Request Amount Limits */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Request Amount Limits</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Bounds on how short or long a single request can be
          </p>
        </div>
        <div className="divide-y border-t border-b">
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="minRequest" className="text-sm font-medium">
                Minimum per request
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Smallest request length allowed
              </p>
            </div>
            <div className="max-w-md">
              <div className="flex items-center gap-2">
                <Input
                  id="minRequest"
                  type="number"
                  min={allowHalfDays ? 0.5 : 1}
                  step={allowHalfDays ? 0.5 : 1}
                  value={minRequestAmount === 0 ? '' : minRequestAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      onFieldChange('minRequestAmount', 0);
                    } else {
                      const num = parseFloat(value);
                      onFieldChange('minRequestAmount', isNaN(num) ? 0 : num);
                    }
                  }}
                  onBlur={(e) => {
                    const normalized = normalizeNumberValue(e.target.value, true);
                    if (allowHalfDays) {
                      // Round to nearest 0.5
                      const rounded = Math.round(normalized * 2) / 2;
                      onFieldChange('minRequestAmount', Math.max(0.5, rounded));
                    } else {
                      // Round to nearest whole number
                      const rounded = Math.round(normalized);
                      onFieldChange('minRequestAmount', Math.max(1, rounded));
                    }
                  }}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">days ({countingMethodLabel})</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="maxRequest" className="text-sm font-medium">
                Maximum per request
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Longest request length allowed (leave empty for no limit)
              </p>
            </div>
            <div className="max-w-md">
              <div className="flex items-center gap-2">
                <Input
                  id="maxRequest"
                  type="number"
                  min={1}
                  value={maxRequestAmount == null ? "" : maxRequestAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      // Send `null`, not `undefined` — `JSON.stringify` drops
                      // undefined keys, so the PATCH body would simply omit
                      // this field and the backend would treat it as "no
                      // change" instead of "clear". `null` survives JSON
                      // serialization and Prisma sets the column to NULL.
                      onFieldChange('maxRequestAmount', null);
                    } else {
                      const num = parseInt(value);
                      onFieldChange('maxRequestAmount', isNaN(num) ? null : num);
                    }
                  }}
                  onBlur={(e) => {
                    const normalized = normalizeNumberValue(e.target.value, false);
                    onFieldChange(
                      'maxRequestAmount',
                      normalized > 0 ? Math.max(1, normalized) : null
                    );
                  }}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">days ({countingMethodLabel})</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notice Periods */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Notice Periods</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            How far in advance requests must or may be submitted
          </p>
        </div>
        <div className="divide-y border-t border-b">
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="minNotice" className="text-sm font-medium">
                Minimum notice
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                0 = can request same-day absence
              </p>
            </div>
            <div className="max-w-md">
              <div className="flex items-center gap-2">
                <Input
                  id="minNotice"
                  type="number"
                  min={0}
                  value={minNoticeDays}
                  onChange={(e) => {
                    // NaN for empty input — React skips DOM update, normalized onBlur
                    onFieldChange('minNoticeDays', e.target.valueAsNumber);
                  }}
                  onBlur={(e) => {
                    // Normalize on blur: empty/NaN → 0, negative → 0
                    const normalized = normalizeNumberValue(e.target.value, false);
                    onFieldChange('minNoticeDays', Math.max(0, normalized || 0));
                  }}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">
                  days ({countingMethodLabel}) before start
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="maxAdvance" className="text-sm font-medium">
                Maximum advance booking
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                How far ahead requests can be scheduled (leave empty for no limit)
              </p>
            </div>
            <div className="max-w-md">
              <div className="flex items-center gap-2">
                <Input
                  id="maxAdvance"
                  type="number"
                  min={1}
                  value={maxAdvanceDays == null ? "" : maxAdvanceDays}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      // See `maxRequestAmount` above for the null-vs-undefined rationale.
                      onFieldChange('maxAdvanceDays', null);
                    } else {
                      const num = parseInt(value);
                      onFieldChange('maxAdvanceDays', isNaN(num) ? null : num);
                    }
                  }}
                  onBlur={(e) => {
                    const normalized = normalizeNumberValue(e.target.value, false);
                    onFieldChange(
                      'maxAdvanceDays',
                      normalized > 0 ? Math.max(1, normalized) : null
                    );
                  }}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">
                  days ({countingMethodLabel}) ahead
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Probation Period */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">During Probation Period</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Whether and how this absence can be taken before probation ends
          </p>
        </div>
        <div className="divide-y border-t border-b">
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label className="text-sm font-medium">Probation rule</Label>
              <p className="text-xs text-muted-foreground mt-1">
                What employees on probation are allowed to do
              </p>
            </div>
            <div className="space-y-2 max-w-md">
              {[
                {
                  value: 'not_allowed',
                  label: 'Not Allowed',
                  desc: 'Cannot take this absence during probation',
                },
                {
                  value: 'allowed',
                  label: 'Allowed Without Limits',
                  desc: 'Full access during probation',
                },
                {
                  value: 'allowed_with_limit',
                  label: 'Allowed Up to Limit',
                  desc: 'Limited days during probation',
                },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() =>
                    onFieldChange(
                      'allowedDuringProbation',
                      option.value as UpdateAbsenceTypeInput['allowedDuringProbation']
                    )
                  }
                  className={`w-full px-3 py-2.5 rounded-md border text-left cursor-pointer transition-colors ${
                    probationAllowance === option.value
                      ? 'border-hygge bg-hygge/5'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <div className="font-medium text-sm">{option.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{option.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {probationAllowance === 'allowed_with_limit' && (
            <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
              <div>
                <Label htmlFor="probationLimit" className="text-sm font-medium">
                  Probation limit
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum days that can be taken during probation
                </p>
              </div>
              <div className="max-w-md">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Maximum</span>
                  <Input
                    id="probationLimit"
                    type="number"
                    min={1}
                    value={probationLimit === 0 ? '' : probationLimit}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        onFieldChange('probationLimit', 0);
                      } else {
                        const num = parseInt(value);
                        onFieldChange('probationLimit', isNaN(num) ? 0 : num);
                      }
                    }}
                    onBlur={(e) => {
                      const normalized = normalizeNumberValue(e.target.value, false);
                      onFieldChange('probationLimit', Math.max(1, normalized));
                    }}
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">days during probation</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// Approvals tab content
function ApprovalsTabContent({
  formData,
  onFieldChange,
  absenceTypeId,
}: TabContentProps & { absenceTypeId: string }) {
  // Aliases for cleaner template
  const approvalRequired = formData.approvalRequired ?? true;
  const managerCanApprove = formData.managerCanApprove ?? false;
  const hrCanApprove = formData.hrCanApprove ?? false;
  const specificApproversEnabled = formData.specificApproversEnabled ?? false;
  const substituteApproversEnabled = formData.substituteApproversEnabled ?? false;
  const autoApproveNoApprover = formData.autoApproveIfNoApprover ?? false;
  const autoApproveAfterDays = formData.autoApproveAfterDays;
  const hasAutoApproveTimer = autoApproveAfterDays != null && autoApproveAfterDays > 0;

  return (
    <div className="space-y-10">
      {/* Approval Required */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Approval Required</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Whether requests need explicit approval before being confirmed
          </p>
        </div>
        <div className="divide-y border-t border-b">
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="approvalRequired" className="text-sm font-medium">
                Require approval
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Requests need to be approved before they're confirmed
              </p>
            </div>
            <div className="max-w-md">
              <Switch
                id="approvalRequired"
                checked={approvalRequired}
                onCheckedChange={(v) => onFieldChange('approvalRequired', v)}
              />
            </div>
          </div>
        </div>
      </section>

      {!approvalRequired ? (
        <div className="border-t border-b py-6 text-center">
          <p className="text-xs text-muted-foreground">
            All requests will be automatically approved.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Useful for unpaid absences or types where tracking is sufficient.
          </p>
        </div>
      ) : (
        <>
          {/* Who Can Approve */}
          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold tracking-tight">Who Can Approve</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Roles and individuals authorized to approve this absence
              </p>
            </div>
            <div className="divide-y border-t border-b">
              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label htmlFor="managerApprove" className="text-sm font-medium">
                    Employee's managers
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    All managers must approve their team's requests
                  </p>
                </div>
                <div className="max-w-md">
                  <Switch
                    id="managerApprove"
                    checked={managerCanApprove}
                    onCheckedChange={(v) => onFieldChange('managerCanApprove', v)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label htmlFor="hrApprove" className="text-sm font-medium">
                    Responsible HR
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    HR team can approve requests
                  </p>
                </div>
                <div className="max-w-md">
                  <Switch
                    id="hrApprove"
                    checked={hrCanApprove}
                    onCheckedChange={(v) => onFieldChange('hrCanApprove', v)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label htmlFor="specificApprovers" className="text-sm font-medium">
                    Specific approvers
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Designate specific employees who can approve this type
                  </p>
                </div>
                <div className="max-w-md">
                  <Switch
                    id="specificApprovers"
                    checked={specificApproversEnabled}
                    onCheckedChange={(v) => onFieldChange('specificApproversEnabled', v)}
                  />
                </div>
              </div>

              {specificApproversEnabled && (
                <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                  <div>
                    <Label className="text-sm font-medium">Selected approvers</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Employees who can approve requests for this absence type
                    </p>
                  </div>
                  <div>
                    <ApproversSection absenceTypeId={absenceTypeId} type="specific" />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label htmlFor="substituteApprovers" className="text-sm font-medium">
                    Substitute approvers
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Backup approvers when primary approvers are unavailable
                  </p>
                </div>
                <div className="max-w-md">
                  <Switch
                    id="substituteApprovers"
                    checked={substituteApproversEnabled}
                    onCheckedChange={(v) => onFieldChange('substituteApproversEnabled', v)}
                  />
                </div>
              </div>

              {substituteApproversEnabled && (
                <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                  <div>
                    <Label className="text-sm font-medium">Selected substitutes</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Backup approvers who can step in when primary approvers are unavailable
                    </p>
                  </div>
                  <div>
                    <ApproversSection absenceTypeId={absenceTypeId} type="substitute" />
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Fallback Rules */}
          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold tracking-tight">Fallback Rules</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                What happens when no one approves in time
              </p>
            </div>
            <div className="divide-y border-t border-b">
              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label htmlFor="autoNoApprover" className="text-sm font-medium">
                    Auto-approve if no approver
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Approve automatically if no one is assigned to approve
                  </p>
                </div>
                <div className="max-w-md">
                  <Switch
                    id="autoNoApprover"
                    checked={autoApproveNoApprover}
                    onCheckedChange={(v) => onFieldChange('autoApproveIfNoApprover', v)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label htmlFor="autoTimer" className="text-sm font-medium">
                    Auto-approve after time
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Approve automatically if not reviewed within deadline
                  </p>
                </div>
                <div className="max-w-md">
                  <Switch
                    id="autoTimer"
                    checked={hasAutoApproveTimer}
                    onCheckedChange={(v) =>
                      // Send `null`, not `undefined` — `JSON.stringify` drops
                      // undefined keys, so the PATCH body would omit this
                      // field and the backend would treat it as "no change",
                      // leaving the old value in the DB. That's what made the
                      // toggle visually flip back to ON right after save (the
                      // bug Dariia reported on "Unpaid absence"). `null`
                      // survives serialization and Prisma sets the column
                      // to NULL.
                      onFieldChange('autoApproveAfterDays', v ? 7 : null)
                    }
                  />
                </div>
              </div>

              {hasAutoApproveTimer && (
                <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                  <div>
                    <Label htmlFor="autoApproveAfterDays" className="text-sm font-medium">
                      Auto-approve deadline
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Number of days before auto-approval kicks in
                    </p>
                  </div>
                  <div className="max-w-md">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Auto-approve after</span>
                      <Input
                        id="autoApproveAfterDays"
                        type="number"
                        min={1}
                        value={autoApproveAfterDays == null ? "" : autoApproveAfterDays}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            onFieldChange('autoApproveAfterDays', null);
                          } else {
                            const num = parseInt(value);
                            onFieldChange('autoApproveAfterDays', isNaN(num) ? null : num);
                          }
                        }}
                        onBlur={(e) => {
                          const normalized = normalizeNumberValue(e.target.value, false);
                          onFieldChange(
                            'autoApproveAfterDays',
                            normalized > 0 ? Math.max(1, normalized) : null
                          );
                        }}
                        className="w-20"
                      />
                      <span className="text-sm text-muted-foreground">days without response</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Approval Flow Preview */}
          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold tracking-tight">Approval Flow Preview</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                How a request will move through the configured approvers
              </p>
            </div>
            <div className="border-t border-b py-4">
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <span className="px-2 py-1 rounded bg-primary/10 text-primary">
                  Request Submitted
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />

                {/* Managers Review */}
                {managerCanApprove && (
                  <>
                    <span className="px-2 py-1 rounded bg-muted">All Managers Review</span>
                    {(specificApproversEnabled || hrCanApprove || autoApproveNoApprover) && (
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </>
                )}

                {/* Specific Approvers */}
                {specificApproversEnabled && (
                  <>
                    <span className="px-2 py-1 rounded bg-muted">Specific Approvers Review</span>
                    {(hrCanApprove || substituteApproversEnabled || autoApproveNoApprover) && (
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </>
                )}

                {/* Substitute Approvers (shown as backup note) */}
                {substituteApproversEnabled && (
                  <>
                    <span className="px-2 py-1 rounded bg-blue-500/10 text-blue-600 border border-blue-200">
                      Substitute Approvers (backup)
                    </span>
                    {(hrCanApprove || autoApproveNoApprover) && (
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </>
                )}

                {/* HR Review */}
                {hrCanApprove && (
                  <>
                    <span className="px-2 py-1 rounded bg-muted">HR Review</span>
                    {!autoApproveNoApprover && (
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </>
                )}

                {/* Auto-approve fallback */}
                {!managerCanApprove &&
                  !specificApproversEnabled &&
                  !hrCanApprove &&
                  autoApproveNoApprover && (
                    <>
                      <span className="px-2 py-1 rounded bg-yellow-500/10 text-yellow-600">
                        Auto-approve
                      </span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </>
                  )}

                <span className="px-2 py-1 rounded bg-green-500/10 text-green-600">Approved</span>
              </div>

              {/* Explanation text */}
              {(managerCanApprove || specificApproversEnabled || hrCanApprove) && (
                <p className="text-xs text-muted-foreground mt-2">
                  {specificApproversEnabled || managerCanApprove || hrCanApprove
                    ? 'All primary approvers must approve the request'
                    : 'Request requires approval'}
                  {substituteApproversEnabled &&
                    '. Substitute approvers can step in when primary approvers are unavailable'}
                </p>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

// Notifications tab content
function NotificationsTabContent({ formData, onFieldChange }: TabContentProps) {
  // Aliases for cleaner template
  const showInCalendar = formData.showInCalendar ?? true;
  const emailNotifications = formData.emailNotifications ?? true;
  const notifyApprovers = formData.notifyApprovers ?? true;
  const notifyRequester = formData.notifyRequester ?? true;
  const attachmentMode = formData.attachmentMode ?? 'optional';
  const allowAttachments = attachmentMode !== 'disabled';
  const allowRecall = formData.allowRecall ?? false;
  const recallDeadlineDays = formData.recallDeadlineDays;
  const requesterCommentRequired = formData.requesterCommentRequired ?? false;
  const approverCommentRequired = formData.approverCommentRequired ?? false;

  return (
    <div className="space-y-10">
      {/* Visibility */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Visibility</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Who can see this absence in shared views
          </p>
        </div>
        <div className="divide-y border-t border-b">
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="showCalendar" className="text-sm font-medium">
                Show in company calendar
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Visible to everyone when enabled. When disabled, only admins, HR, and the
                requester can see it.
              </p>
            </div>
            <div className="max-w-md">
              <Switch
                id="showCalendar"
                checked={showInCalendar}
                onCheckedChange={(v) => onFieldChange('showInCalendar', v)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Email Notifications */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Email Notifications</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Automated emails sent during the request lifecycle
          </p>
        </div>
        <div className="divide-y border-t border-b">
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="emailNotif" className="text-sm font-medium">
                Email notifications
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Send emails for request updates
              </p>
            </div>
            <div className="max-w-md">
              <Switch
                id="emailNotif"
                checked={emailNotifications}
                onCheckedChange={(v) => onFieldChange('emailNotifications', v)}
              />
            </div>
          </div>

          {emailNotifications && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label htmlFor="notifyApprovers" className="text-sm font-medium">
                    Notify approvers
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Email approvers when a new request is submitted
                  </p>
                </div>
                <div className="max-w-md">
                  <Switch
                    id="notifyApprovers"
                    checked={notifyApprovers}
                    onCheckedChange={(v) => onFieldChange('notifyApprovers', v)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
                <div>
                  <Label htmlFor="notifyRequester" className="text-sm font-medium">
                    Notify requester
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Email employee when request is approved/rejected
                  </p>
                </div>
                <div className="max-w-md">
                  <Switch
                    id="notifyRequester"
                    checked={notifyRequester}
                    onCheckedChange={(v) => onFieldChange('notifyRequester', v)}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Attachments */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Attachments</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Whether employees can attach supporting files to a request
          </p>
        </div>
        <div className="divide-y border-t border-b">
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="attachments" className="text-sm font-medium">
                Allow attachments
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Employees can attach files (e.g., medical certificates)
              </p>
            </div>
            <div className="max-w-md">
              <Switch
                id="attachments"
                checked={allowAttachments}
                onCheckedChange={(v) =>
                  onFieldChange('attachmentMode', v ? 'optional' : 'disabled')
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recall */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Recall</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Whether and how an approved absence can be cancelled
          </p>
        </div>
        <div className="divide-y border-t border-b">
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="allowRecall" className="text-sm font-medium">
                Allow request recall
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Employees can cancel their approved requests
              </p>
            </div>
            <div className="max-w-md">
              <Switch
                id="allowRecall"
                checked={allowRecall}
                onCheckedChange={(v) => onFieldChange('allowRecall', v)}
              />
            </div>
          </div>

          {allowRecall && (
            <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
              <div>
                <Label htmlFor="recallDeadlineDays" className="text-sm font-medium">
                  Recall deadline
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  How close to the start date recalls are still allowed
                </p>
              </div>
              <div className="max-w-md">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Recall allowed until</span>
                  <Input
                    id="recallDeadlineDays"
                    type="number"
                    min={0}
                    value={recallDeadlineDays == null ? '' : recallDeadlineDays.toString()}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === '-') {
                        // See `maxRequestAmount` for null-vs-undefined rationale.
                        onFieldChange('recallDeadlineDays', null);
                      } else {
                        const num = parseInt(value);
                        if (!isNaN(num) && num >= 0) {
                          onFieldChange('recallDeadlineDays', num);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      const normalized = normalizeNumberValue(e.target.value, false);
                      if (normalized >= 0) {
                        onFieldChange('recallDeadlineDays', normalized);
                      } else {
                        onFieldChange('recallDeadlineDays', null);
                      }
                    }}
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">days before absence starts</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Comment Requirements */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Comment Requirements</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Whether comments are required at submission and decision time
          </p>
        </div>
        <div className="divide-y border-t border-b">
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="requesterComment" className="text-sm font-medium">
                Requester must add comment
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Require a reason when submitting request
              </p>
            </div>
            <div className="max-w-md">
              <Switch
                id="requesterComment"
                checked={requesterCommentRequired}
                onCheckedChange={(v) => onFieldChange('requesterCommentRequired', v)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-x-8 gap-y-2 py-4">
            <div>
              <Label htmlFor="approverComment" className="text-sm font-medium">
                Approver must add comment
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Require a note when approving/rejecting
              </p>
            </div>
            <div className="max-w-md">
              <Switch
                id="approverComment"
                checked={approverCommentRequired}
                onCheckedChange={(v) => onFieldChange('approverCommentRequired', v)}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Employment Type Chips for assignments
function EmploymentTypeAssignments({
  absenceTypeId,
  assignedEmploymentTypeIds,
  onChanged,
}: {
  absenceTypeId: string;
  assignedEmploymentTypeIds: string[];
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: employmentTypes } = useEmploymentTypes();

  const handleMutationError = (action: 'assign' | 'unassign', employmentTypeId: string) => {
    const name =
      employmentTypes?.find((t) => t.id === employmentTypeId)?.name ?? 'employment type';
    toast.error(
      action === 'assign'
        ? `Failed to assign "${name}". Please try again.`
        : `Failed to unassign "${name}". Please try again.`
    );
  };

  const assignMutation = useMutation({
    mutationFn: (employmentTypeId: string) =>
      assignAbsenceTypeToEmploymentType(absenceTypeId, employmentTypeId),
    onSuccess: (_data, employmentTypeId) => {
      onChanged();
      queryClient.invalidateQueries({ queryKey: ['employee-policies'] });
      const et = employmentTypes?.find((t) => t.id === employmentTypeId);
      const count = et?._count?.employees ?? 0;
      toast.success(
        `Assigned to "${et?.name ?? 'employment type'}"${
          count > 0 ? ` — ${count} employee${count === 1 ? '' : 's'} auto-included` : ''
        }`
      );
    },
    onError: (_err, employmentTypeId) => handleMutationError('assign', employmentTypeId),
  });

  const unassignMutation = useMutation({
    mutationFn: (employmentTypeId: string) =>
      unassignAbsenceTypeFromEmploymentType(absenceTypeId, employmentTypeId),
    onSuccess: (_data, employmentTypeId) => {
      onChanged();
      queryClient.invalidateQueries({ queryKey: ['employee-policies'] });
      const name =
        employmentTypes?.find((t) => t.id === employmentTypeId)?.name ?? 'employment type';
      toast.success(`Unassigned from "${name}"`);
    },
    onError: (_err, employmentTypeId) => handleMutationError('unassign', employmentTypeId),
  });

  const activeTypes = employmentTypes?.filter((t) => t.isActive) ?? [];
  const assignedSet = new Set(assignedEmploymentTypeIds);

  const handleToggle = (id: string) => {
    if (assignMutation.isPending || unassignMutation.isPending) return;
    if (assignedSet.has(id)) {
      unassignMutation.mutate(id);
    } else {
      assignMutation.mutate(id);
    }
  };

  const isPending = assignMutation.isPending || unassignMutation.isPending;

  if (activeTypes.length === 0) return null;

  return (
    <div className="mb-6 rounded-lg border border-border p-4">
      <h4 className="mb-1 text-sm font-medium">Employment Types</h4>
      <p className="mb-3 text-xs text-muted-foreground">
        All employees with the selected employment type are automatically added to the Selected
        list and locked there until the type is unlinked.
      </p>
      <div className="flex flex-wrap gap-2">
        {activeTypes.map((et) => {
          const isAssigned = assignedSet.has(et.id);
          const pendingThis =
            (assignMutation.isPending && assignMutation.variables === et.id) ||
            (unassignMutation.isPending && unassignMutation.variables === et.id);
          return (
            <button
              key={et.id}
              type="button"
              onClick={() => handleToggle(et.id)}
              disabled={isPending}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                isAssigned
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-background text-muted-foreground hover:bg-muted'
              } ${isPending ? 'opacity-50' : ''}`}
            >
              {pendingThis ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                isAssigned && <Check className="h-3 w-3" />
              )}
              {et.name}
              {et._count?.employees != null && (
                <span className="text-[10px] opacity-60">({et._count.employees})</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// BALANCE MIGRATION — move a sub-group of employees from one absence type
// (typically the default Vacation) into this one (e.g. "Vacation pre-2021"
// for HR carving out employees on a different PF policy). Carries balance,
// PF history, audit log and the assignment across, and writes a per-employee
// PF mapping override so subsequent /peopleforce/sync-absence-balances runs
// route those employees here instead of bouncing them back to the source.
// ============================================================================

function BalanceMigrationCard({
  targetAbsenceType,
  onMigrated,
}: {
  targetAbsenceType: AbsenceType;
  onMigrated: () => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <div className="rounded-xl border border-dashed border-border/60 bg-card/40 p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h4 className="text-[13px] font-semibold tracking-tight flex items-center gap-2">
              <ArrowRightLeft className="h-3.5 w-3.5 text-muted-foreground" />
              Migrate balance from another type
            </h4>
            <p className="text-[12px] text-muted-foreground mt-1 leading-snug max-w-xl">
              Use this to carve out a sub-group from another absence type into{' '}
              <span className="font-medium text-foreground">{targetAbsenceType.name}</span>.
              Moves their balance, PeopleForce-imported history and assignment, and locks in
              a per-employee mapping so future PF syncs don&apos;t drag them back to the
              source type.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDialogOpen(true)}
            className="shrink-0 cursor-pointer"
          >
            Open migration
          </Button>
        </div>
      </div>
      <BalanceMigrationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        targetAbsenceType={targetAbsenceType}
        onMigrated={() => {
          setDialogOpen(false);
          onMigrated();
        }}
      />
    </>
  );
}

function BalanceMigrationDialog({
  open,
  onOpenChange,
  targetAbsenceType,
  onMigrated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetAbsenceType: AbsenceType;
  onMigrated: () => void;
}) {
  const formatName = useEmployeeNameFormatter();
  const [sourceTypeId, setSourceTypeId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Reset selection on close.
  useEffect(() => {
    if (!open) {
      setSourceTypeId(null);
      setSelectedIds(new Set());
      setSearchQuery('');
    }
  }, [open]);

  // All absence types (so user can pick a source).
  const { data: allTypes } = useQuery({
    queryKey: ['absence-types'],
    queryFn: getAbsenceTypes,
    enabled: open,
  });

  // Source type's current assignees — the only candidates for migration.
  const { data: sourceAssignments, isFetching: assignmentsFetching } = useQuery({
    queryKey: ['absence-types', sourceTypeId, 'assignments'],
    queryFn: () => getAbsenceTypeAssignments(sourceTypeId!),
    enabled: !!sourceTypeId,
  });

  const candidates = useMemo(() => sourceAssignments?.employees ?? [], [sourceAssignments]);

  // Filtered view — search matches first/last/preferred name, position title and department.
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredCandidates = useMemo(() => {
    if (!normalizedQuery) return candidates;
    return candidates.filter((emp) => {
      const haystack = [
        emp.firstName,
        emp.lastName,
        emp.preferredName,
        emp.position?.title,
        emp.department?.name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [candidates, normalizedQuery]);

  // "Select all" applies to the currently visible (filtered) candidates so the
  // user can drill down to a subset and select just that subset.
  const visibleSelectedCount = filteredCandidates.filter((c) => selectedIds.has(c.id)).length;
  const allVisibleSelected =
    filteredCandidates.length > 0 && visibleSelectedCount === filteredCandidates.length;

  const mutation = useMutation({
    mutationFn: () =>
      migrateBalanceBetweenAbsenceTypes(
        targetAbsenceType.id,
        sourceTypeId!,
        Array.from(selectedIds)
      ),
    onSuccess: (result) => {
      toast.success(
        `Migrated ${result.migratedEmployees} employee${result.migratedEmployees === 1 ? '' : 's'}`,
        {
          description: `${result.movedBalances} balance row(s), ${result.movedHistoryRecords} history record(s), ${result.overrideMappingsWritten} PF mapping override(s) written.`,
        }
      );
      onMigrated();
    },
    onError: (err: Error) => {
      toast.error('Migration failed', { description: err.message });
    },
  });

  const sourceOptions = useMemo(
    () => (allTypes ?? []).filter((t) => t.id !== targetAbsenceType.id && t.isActive),
    [allTypes, targetAbsenceType.id]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] gap-0 p-0 overflow-x-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <ArrowRightLeft className="h-4 w-4" />
            Migrate balance into {targetAbsenceType.name}
          </DialogTitle>
          <DialogDescription className="text-xs">
            For each selected employee, current balance, PeopleForce history, audit log and
            assignment move to <span className="font-medium">{targetAbsenceType.name}</span>.
            A per-employee PF mapping override is written so future syncs respect the change.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Source type select */}
          <div>
            <Label className="text-[12px] font-medium">Source type</Label>
            <select
              value={sourceTypeId ?? ''}
              onChange={(e) => {
                setSourceTypeId(e.target.value || null);
                setSelectedIds(new Set());
              }}
              className="mt-1.5 h-9 w-full rounded-md border border-input bg-background px-3 text-sm cursor-pointer"
            >
              <option value="">— Pick the type to move employees FROM —</option>
              {sourceOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">
              Only employees currently assigned to this type appear below.
            </p>
          </div>

          {/* Employee picker */}
          {sourceTypeId && (
            <div>
              <div className="flex items-baseline justify-between mb-1.5">
                <Label className="text-[12px] font-medium">
                  Employees ({selectedIds.size}/{candidates.length} selected
                  {normalizedQuery && candidates.length !== filteredCandidates.length
                    ? `, ${filteredCandidates.length} shown`
                    : ''}
                  )
                </Label>
                {filteredCandidates.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const next = new Set(selectedIds);
                      if (allVisibleSelected) {
                        filteredCandidates.forEach((c) => next.delete(c.id));
                      } else {
                        filteredCandidates.forEach((c) => next.add(c.id));
                      }
                      setSelectedIds(next);
                    }}
                    className="text-[11px] text-muted-foreground hover:text-foreground cursor-pointer"
                  >
                    {allVisibleSelected
                      ? normalizedQuery
                        ? 'Deselect shown'
                        : 'Deselect all'
                      : normalizedQuery
                        ? 'Select shown'
                        : 'Select all'}
                  </button>
                )}
              </div>
              {/* Search input — show as soon as a source type is picked so the
                  control position is stable while assignments are loading. */}
              {candidates.length > 0 && (
                <div className="relative mb-1.5">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                  <Input
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name, position or department…"
                    className="h-8 pl-8 pr-8 text-[13px]"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground cursor-pointer"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              )}
              {assignmentsFetching ? (
                <div className="h-32 rounded-md border bg-muted/30 flex items-center justify-center text-xs text-muted-foreground">
                  Loading employees…
                </div>
              ) : candidates.length === 0 ? (
                <div className="h-24 rounded-md border bg-muted/30 flex items-center justify-center text-xs text-muted-foreground">
                  No employees are currently assigned to this type.
                </div>
              ) : filteredCandidates.length === 0 ? (
                <div className="h-24 rounded-md border bg-muted/30 flex items-center justify-center text-xs text-muted-foreground">
                  No employees match &ldquo;{searchQuery}&rdquo;.
                </div>
              ) : (
                <div className="rounded-md border max-h-72 overflow-y-auto divide-y divide-border/50">
                  {filteredCandidates.map((emp) => {
                    const checked = selectedIds.has(emp.id);
                    return (
                      <label
                        key={emp.id}
                        className="flex items-center gap-2.5 px-3 py-2 hover:bg-muted/40 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = new Set(selectedIds);
                            if (checked) next.delete(emp.id);
                            else next.add(emp.id);
                            setSelectedIds(next);
                          }}
                          className="cursor-pointer"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] truncate">
                            {formatName({ firstName: emp.firstName, lastName: emp.lastName })}
                            {emp.preferredName && (
                              <span className="text-muted-foreground"> ({emp.preferredName})</span>
                            )}
                          </p>
                          <p className="text-[11px] text-muted-foreground truncate">
                            {emp.position?.title ?? ''}
                            {emp.position && emp.department ? ' · ' : ''}
                            {emp.department?.name ?? ''}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="px-5 py-3 border-t bg-muted/20">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!sourceTypeId || selectedIds.size === 0 || mutation.isPending}
            className="cursor-pointer gap-1.5"
          >
            {mutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ArrowRightLeft className="h-3.5 w-3.5" />
            )}
            Migrate {selectedIds.size > 0 ? selectedIds.size : ''}{' '}
            {selectedIds.size === 1 ? 'employee' : 'employees'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Assignments tab content
function AssignmentsTabContent({ absenceType }: { absenceType: AbsenceType }) {
  const queryClient = useQueryClient();

  // Fetch current assignments
  const { data: assignments, refetch: refetchAssignments } = useQuery({
    queryKey: ['absence-types', absenceType.id, 'assignments'],
    queryFn: () => getAbsenceTypeAssignments(absenceType.id),
  });

  const assignedEmploymentTypeIds = useMemo(
    () => assignments?.employmentTypes?.map((et) => et.id) ?? [],
    [assignments]
  );

  // Convert API assignments to PickerSelection: direct employees first, then
  // employment-type employees as locked entries (auto-included, cannot be removed
  // from the picker).
  const initialSelection = useMemo((): PickerSelection => {
    if (!assignments) return createEmptySelection();

    const direct = assignments.employees.map((emp) => ({
      id: emp.id,
      firstName: emp.firstName,
      lastName: emp.lastName,
      preferredName: emp.preferredName,
      avatarUrl: emp.avatarUrl,
      workEmail: emp.workEmail ?? '',
      department: emp.department,
      position: emp.position ?? null,
    }));

    const directIds = new Set(direct.map((e) => e.id));
    const locked = (assignments.employmentTypeEmployees ?? [])
      .filter((emp) => !directIds.has(emp.id))
      .map((emp) => ({
        id: emp.id,
        firstName: emp.firstName,
        lastName: emp.lastName,
        preferredName: emp.preferredName,
        avatarUrl: emp.avatarUrl,
        workEmail: emp.workEmail ?? '',
        department: emp.department,
        position: emp.position ?? null,
        locked: true,
        lockedReason: emp.viaEmploymentTypeName
          ? `via ${emp.viaEmploymentTypeName}`
          : 'via employment type',
      }));

    return {
      employees: [...direct, ...locked],
      departments: [] as never[],
    };
  }, [assignments]);

  // Local state for picker
  const [selection, setSelection] = useState<PickerSelection>(createEmptySelection());
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoadingAll, setIsLoadingAll] = useState(false);

  // Sync initial selection when data loads — fingerprint covers both direct and
  // locked (employment-type-derived) employees so flips on the chip refresh too.
  const assignmentsRef = JSON.stringify({
    direct: assignments?.employees.map((e) => e.id) ?? [],
    locked: assignments?.employmentTypeEmployees?.map((e) => e.id) ?? [],
  });
  useEffect(() => {
    if (assignments) {
      setSelection(initialSelection);
      setHasChanges(false);
    }
  }, [assignmentsRef]);

  // Handle selection change
  const handleSelectionChange = (newSelection: PickerSelection) => {
    setSelection(newSelection);
    setHasChanges(true);
  };

  // Handle "Select All" - fetch all current staff (active + probation) and add them as direct
  // selection. Locked entries from the existing selection are preserved.
  const handleSelectAll = async () => {
    setIsLoadingAll(true);
    try {
      const response = await getEmployees({ statuses: ['active', 'probation'], limit: 1000, page: 1 });
      const lockedById = new Map(
        selection.employees.filter((e) => e.locked).map((e) => [e.id, e])
      );
      const direct = response.data
        .filter((emp: EmployeeListItem) => !lockedById.has(emp.id))
        .map((emp: EmployeeListItem) => ({
          id: emp.id,
          firstName: emp.firstName,
          lastName: emp.lastName,
          preferredName: emp.preferredName,
          avatarUrl: emp.avatarUrl,
          workEmail: emp.workEmail,
          department: emp.department,
          position: emp.position,
        }));

      setSelection({
        employees: [...direct, ...lockedById.values()],
        departments: [] as never[],
      });
      setHasChanges(true);
    } finally {
      setIsLoadingAll(false);
    }
  };

  // Mutation for batch saving employees
  const batchAssignMutation = useMutation({
    mutationFn: (employeeIds: string[]) => batchAssignAbsenceType(absenceType.id, employeeIds),
    onSuccess: () => {
      refetchAssignments();
      queryClient.invalidateQueries({ queryKey: ['absence-types', absenceType.id] });
      queryClient.invalidateQueries({ queryKey: ['absence-types'] });
      // Invalidate employee assigned absence types so assignments update immediately
      queryClient.invalidateQueries({ queryKey: ['employee-assigned-absence-types'] });
      // Invalidate employee policies cache so profiles show updated assignments (legacy)
      queryClient.invalidateQueries({ queryKey: ['employee-policies'] });
      setHasChanges(false);
    },
  });

  // Save all changes with single API call. Locked (employment-type-derived) entries
  // are not direct assignments and must not be persisted via the batch endpoint.
  const handleSave = async () => {
    const employeeIds = selection.employees.filter((e) => !e.locked).map((e) => e.id);
    await batchAssignMutation.mutateAsync(employeeIds);
  };

  // Force-exclude a locked (group-inherited) employee. The DELETE endpoint
  // upserts a per-employee `excluded=true` override on the backend, which
  // removes that one employee from group-derived coverage without unlinking
  // the whole employment-type assignment. Persists immediately (not via the
  // batch Save below) so the row vanishes from the locked list right away.
  const handleForceRemoveLocked = async (employeeId: string) => {
    try {
      await unassignEmployeeFromAbsenceType(absenceType.id, employeeId);
      await refetchAssignments();
      queryClient.invalidateQueries({ queryKey: ['absence-types', absenceType.id] });
      queryClient.invalidateQueries({ queryKey: ['employee-assigned-absence-types'] });
      queryClient.invalidateQueries({ queryKey: ['employee-policies'] });
      toast.success('Employee excluded from this absence type');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to exclude employee';
      toast.error(message);
    }
  };

  const isSaving = batchAssignMutation.isPending;

  return (
    <div className="space-y-6">
      <EmploymentTypeAssignments
        absenceTypeId={absenceType.id}
        assignedEmploymentTypeIds={assignedEmploymentTypeIds}
        onChanged={refetchAssignments}
      />
      <EmployeePicker
        value={selection}
        onChange={handleSelectionChange}
        onSave={handleSave}
        isSaving={isSaving}
        hasChanges={hasChanges}
        showSaveButton={true}
        saveButtonText="Save Assignments"
        onSelectAll={handleSelectAll}
        isLoadingAll={isLoadingAll}
        onForceRemoveLocked={handleForceRemoveLocked}
      />

      <BalanceMigrationCard
        targetAbsenceType={absenceType}
        onMigrated={() => {
          refetchAssignments();
          // Also refresh anything that depends on per-employee absence data
          // — the migration may have moved their assignments + balances away
          // from another type.
          queryClient.invalidateQueries({ queryKey: ['absence-types'] });
          queryClient.invalidateQueries({ queryKey: ['employee-absences'] });
          queryClient.invalidateQueries({ queryKey: ['employee-assigned-absence-types'] });
        }}
      />
    </div>
  );
}
